import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/cc";
import { Landmark, ShieldAlert, Archive, Network } from "lucide-react";

const pretty = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Private reciprocal Culture & Heritage links for a canonical Entity Profile.
// Shows linked Culture records (with the three independent dimensions, access + archived state)
// and cultural relationships involving this entity. Never exposed on public entity pages.
export function CultReciprocal({ id }) {
  const [records, setRecords] = useState([]);
  const [rels, setRels] = useState([]);

  useEffect(() => {
    if (!id) return;
    api.get(`/culture/for-entity/${id}`).then((r) => setRecords(r.data)).catch(() => {});
    api.get(`/culture/relationships`, { params: { entity: id } }).then((r) => setRels(r.data)).catch(() => {});
  }, [id]);

  return (
    <Card className="p-4" data-testid="cult-reciprocal-entity">
      <div className="label-caps text-zinc-500 text-[0.6rem] mb-3 flex items-center gap-2"><Landmark className="h-3.5 w-3.5 text-amber-400" /> Linked Culture & Heritage Intelligence</div>

      {records.length === 0 && rels.length === 0 && (
        <p className="text-xs text-zinc-600" data-testid="cult-reciprocal-empty">No linked culture records or relationships.</p>
      )}

      {records.length > 0 && (
        <div className="space-y-1.5 mb-3">
          <div className="text-[0.6rem] text-zinc-600 uppercase">Records</div>
          {records.map((r) => (
            <a key={r.id} href={`/command-center/culture/${r.id}`} data-testid={`cult-recip-rec-${r.id}`} className="block text-xs border-b border-zinc-900 pb-1.5 hover:bg-zinc-900/40 rounded-sm px-1">
              <div className="flex items-center gap-1.5">
                {r.is_sensitive && <ShieldAlert className="h-3 w-3 text-red-400" title="Sensitive" />}
                {r.is_archived && <Archive className="h-3 w-3 text-zinc-500" title="Archived" />}
                <span className={`text-zinc-200 font-medium ${r.is_archived ? "line-through opacity-60" : ""}`}>{r.title}</span>
                {r.is_archived && <span className="text-[0.55rem] text-zinc-500 uppercase">archived</span>}
              </div>
              <div className="text-zinc-500 mt-0.5 flex flex-wrap gap-x-2 text-[0.65rem]">
                <span>{pretty(r.record_type)}</span>
                <span className="text-emerald-400/80">E: {pretty(r.editorial_verification)}</span>
                <span className="text-fuchsia-300/80">Cm: {pretty(r.community_validation)}</span>
                <span className="text-sky-300/80">H: {pretty(r.historical_certainty)}</span>
                <span>· {pretty(r.access_classification)}</span>
              </div>
            </a>
          ))}
        </div>
      )}

      {rels.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[0.6rem] text-zinc-600 uppercase flex items-center gap-1"><Network className="h-3 w-3" /> Relationships</div>
          {rels.map((r) => (
            <div key={r.id} data-testid={`cult-recip-rel-${r.id}`} className="text-[0.65rem] border-b border-zinc-900 pb-1.5 px-1">
              <div className="text-zinc-300">
                {r.subject?.name || "?"} <span className="text-amber-300/80">{r.access_restricted ? "restricted relationship" : pretty(r.relationship_type)}</span> {r.object?.name || "?"}
                {r.is_archived && <span className="text-[0.55rem] text-zinc-500 uppercase ml-1">· archived</span>}
              </div>
              {!r.access_restricted && (
                <div className="text-zinc-500 flex flex-wrap gap-x-2">
                  <span className="text-emerald-400/80">E: {pretty(r.editorial_verification)}</span>
                  <span className="text-fuchsia-300/80">Cm: {pretty(r.community_validation)}</span>
                  {r.historical_certainty && <span className="text-sky-300/80">H: {pretty(r.historical_certainty)}</span>}
                </div>
              )}
              {r.access_restricted && <div className="text-red-300/80 flex items-center gap-1"><ShieldAlert className="h-3 w-3" /> Protected details withheld.</div>}
            </div>
          ))}
        </div>
      )}
      <a href={`/command-center/culture/graph?entity=${id}`} data-testid="cult-recip-graph-link" className="mt-3 inline-flex items-center gap-1 text-[0.65rem] text-amber-300 hover:underline"><Network className="h-3 w-3" /> Open relationship graph</a>
    </Card>
  );
}
